import { useEffect } from "react";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Building2, KeyRound, ShieldCheck } from "lucide-react";
import { paths } from "../../app/paths.js";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { ApiClientError } from "../../api/http.js";
import { useClaimInvite, useInvitePreview, useJoinInvite } from "../../data/teams.js";
import { readInviteSecret } from "../../lib/invite.js";
import { ROLE_LABEL, describeTeamError } from "../../lib/teams.js";
import { useToast } from "../../ui/Toast.js";
import { Alert, Badge, Button, Card, EmptyState, Page, PageHeader, Skeleton, buttonClass } from "../../ui/primitives.js";

function previewError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 404) {
      return "Diese Einladung ist ungültig, abgelaufen oder wurde bereits verwendet.";
    }
    if (err.status === 403) {
      return "Diese Einladung gehört zu einem anderen Konto. Melde dich mit der eingeladenen E-Mail-Adresse an.";
    }
  }
  return describeTeamError(err);
}

export function AcceptInvitePage(): ReactNode {
  useDocumentTitle("Einladung");
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const secret = readInviteSecret(location.hash);
  const preview = useInvitePreview(secret);
  const join = useJoinInvite();
  const claim = useClaimInvite();
  const lookup = preview.data;
  const claimMutate = claim.mutate;
  const claimIdle = claim.isIdle;

  // Park the invite on this account right away, so "Später" or closing the tab does not lose it.
  useEffect((): void => {
    if (secret !== undefined && lookup !== undefined && lookup.claimed === false && lookup.stale !== true && claimIdle) {
      claimMutate({ secretB64: secret, lookup });
    }
  }, [secret, lookup, claimIdle, claimMutate]);

  if (secret === undefined) {
    return (
      <Page width="narrow">
        <EmptyState icon={Building2} title="Ungültiger Einladungslink">
          Der Link ist unvollständig. Bitte öffne den Link aus der Einladung erneut oder lass dir einen neuen schicken.
        </EmptyState>
      </Page>
    );
  }

  if (preview.isPending) {
    return (
      <Page width="narrow">
        <Skeleton className="h-56" />
      </Page>
    );
  }

  if (preview.isError) {
    const alreadyMember = preview.error instanceof ApiClientError && preview.error.status === 409;
    return (
      <Page width="narrow">
        <EmptyState icon={Building2} title={alreadyMember ? "Du bist bereits dabei" : "Einladung nicht verfügbar"}>
          {alreadyMember ? "Dieses Team ist bereits in deiner Liste." : previewError(preview.error)}
          {alreadyMember ? (
            <span className="mt-6 block">
              <Link to={paths.teams} className="text-flow underline-offset-4 hover:underline">
                Zu deinen Teams
              </Link>
            </span>
          ) : null}
        </EmptyState>
      </Page>
    );
  }

  if (lookup === undefined) {
    return null;
  }

  if (lookup.stale === true) {
    return (
      <Page width="narrow">
        <EmptyState
          icon={KeyRound}
          title="Einladung ungültig"
          action={<Link to={paths.teams} className={buttonClass("secondary", "md")}>Zu deinen Teams</Link>}
        >
          Der Schlüssel von {lookup.orgName} wurde erneuert, nachdem diese Einladung erstellt wurde. Sie funktioniert
          deshalb nicht mehr. Bitte eine Person mit Admin-Rechten im Team, dich neu einzuladen.
        </EmptyState>
      </Page>
    );
  }
  const needs2fa = lookup.require2fa === true && lookup.twoFactorReady !== true;
  const saved = lookup.claimed === true || claim.isSuccess;
  return (
    <Page width="narrow">
      <PageHeader eyebrow="Einladung" title={lookup.orgName} description="Du wurdest in ein Team eingeladen." />
      <Card className="mt-7 max-w-xl">
        <div className="flex flex-col gap-5 p-5">
          <div className="flex items-start gap-3.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-lg border border-line bg-ink-3 text-flow">
              <ShieldCheck size={17} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-foam">
                Du wirst als <Badge tone="mint">{ROLE_LABEL[lookup.role]}</Badge> ins Team aufgenommen.
              </p>
              <p className="mt-2 text-sm leading-relaxed text-mist">
                Der Team-Schlüssel wird im Browser für dein Konto gesichert. Der Server erhält ihn nur verschlüsselt.
              </p>
            </div>
          </div>
          {needs2fa ? (
            <Alert>
              Dieses Team verlangt Zwei-Faktor-Authentifizierung. Du kannst beitreten, siehst die Einträge aber erst,
              wenn 2FA eingerichtet ist.{" "}
              <Link to={`${paths.settings}?setup=totp`} className="text-flow underline-offset-4 hover:underline">
                2FA einrichten
              </Link>
            </Alert>
          ) : null}
          {join.isError ? <Alert tone="danger">{describeTeamError(join.error)}</Alert> : null}
          <div className="flex flex-wrap gap-2 border-t border-line pt-4">
            <Button
              loading={join.isPending}
              onClick={(): void => {
                join.mutate(
                  { secretB64: secret, lookup },
                  {
                    onSuccess: (accepted): void => {
                      toast.notify(`Willkommen im Team ${accepted.orgName}.`, { tone: "success" });
                      void navigate(paths.team(accepted.orgId), { replace: true });
                    }
                  }
                );
              }}
            >
              Team beitreten
            </Button>
            <Button
              variant="ghost"
              onClick={(): void => {
                if (saved) {
                  toast.notify("Einladung gespeichert. Du findest sie jederzeit unter Teams.");
                }
                void navigate(paths.teams);
              }}
            >
              Später
            </Button>
          </div>
        </div>
      </Card>
    </Page>
  );
}
